/**
 * @author NikolaV
 */
import { Events, EventsGlobal } from '@shared/Events';
import { Button } from '@shared/ui-react/Button';
import { ReactUtils } from '@shared/ui-react/ReactUtils';
import { cs } from '@shared/utils/utils-classname';
import { makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { Rnd } from 'react-rnd';
import './FloatingWindow.scss';

let zIndexTop = 1002;
let shiftKeyDown = false;
const lastPositionStorage: Record<string, IFloatingWindowSizing> = {};
const windows: Record<string, IFloatingWindow> = {};
const isMobile = () => (document.querySelector('.sidebar-panel') as HTMLDivElement).classList.contains('mobile');

export interface IFloatingWindow {
    group: string;
    id: string;
    uuid?: string; // group + id
    title?: string;
    collapsedTitle?: string;
    body: any;
    getState?: () => any;
    showCloseButton?: boolean;
    allowMultiple?: boolean;
    preventCloseOthers?: boolean;
    onClose?: Function;
    onKeyDown?: Function;
    resize?: null | 'both' | 'horizontal' | 'vertical';
    defaultSizing?: IFloatingWindowSizing;
    disableAutoLayout?: boolean; // by default, window will use previous sizing of same group and offset x/y a bit with multiple instances
    zIndex?: number;
    children?: React.ReactNode;
    componentInstance?: FloatingWindow;
}

interface IFloatingWindowSizing {
    width?: number | 'auto'; // auto only for non-resizable
    height?: number | 'auto';
    x?: number;
    y?: number;
}

export interface IFloatingWindowStoredState {
    group: string;
    id: string;
    sizing: IFloatingWindowSizing;
    zIndex: number;
    data: any;
}

$(document).ready(() => {
    // send keyboard events to topmost window
    document.addEventListener('keydown', e => {
        if (e.key === 'Control') shiftKeyDown = true;

        const topmostWindow = WindowManager.getTopmostUuid();
        if (topmostWindow) {
            if (e.key === 'Escape') {
                WindowManager.close(topmostWindow.uuid);
            } else if (typeof topmostWindow?.onKeyDown === 'function') {
                if (window.document.activeElement.tagName !== 'INPUT' && window.document.activeElement.tagName !== 'TEXTAREA') {
                    topmostWindow.onKeyDown(e);
                }
            }
        }
    });
    document.addEventListener('keyup', e => {
        if (e.key === 'Control') shiftKeyDown = false;
    });

    $('#map-draggable-dialog-bounds').remove();
    $('<div id="map-draggable-dialog-bounds">').appendTo('body');
});

function wrapperEl() {
    const existing = document.querySelector('#window-manager-wrapper') as HTMLDivElement;
    if (existing) {
        return existing;
    }
    const el = $('<div id="window-manager-wrapper">').get(0) as HTMLDivElement;
    document.body.appendChild(el);
    return el;
}

export const WindowManager = {
    open: (opts: IFloatingWindow) => {
        if (isMobile()) {
            WindowManager.closeAll();
        }
        wrapperEl().style.zIndex = isMobile() ? '1000' : '1002';

        opts.uuid = opts.group + '-' + opts.id;
        if (windows[opts.uuid]) {
            return;
        }

        let componentInstance: FloatingWindow = null;
        if (typeof opts.getState === 'function') {
            const getBodyData = opts.getState;
            opts.getState = (): IFloatingWindowStoredState => {
                const data = getBodyData();
                if (!data) return null;

                return {
                    group: opts.group,
                    id: opts.id,
                    sizing: componentInstance.position,
                    zIndex: parseInt(windowEl.style.zIndex, 10),
                    data,
                };
            };
        }
        windows[opts.uuid] = opts;

        // close existing from same group
        if (!opts.preventCloseOthers && (!opts.allowMultiple || (opts.allowMultiple && !shiftKeyDown))) {
            const existing = Array.from(document.querySelectorAll(`[data-window-group="${opts.group}"`)) as HTMLDivElement[];
            existing.forEach(el => WindowManager.close(el.dataset.windowUuid));
        }

        zIndexTop = Math.max(opts.zIndex ?? 0, zIndexTop) + 1;
        let zIndex = opts.zIndex ?? zIndexTop;
        const windowEl: HTMLDivElement = $(
            `<div data-window-group="${opts.group}" data-window-uuid="${opts.uuid}" style="position: absolute; top: 0; left: 0; width: 1px; height: 1px; z-index: ${zIndex}">`
        ).get(0) as HTMLDivElement;
        wrapperEl().appendChild(windowEl);

        windowEl.onmousedown = () => {
            if (parseInt(windowEl.style.zIndex) < zIndexTop) {
                zIndexTop += 1;
                windowEl.style.zIndex = zIndexTop + '';
            }
        };

        windowEl.onpointerup = e => {
            // stop clicks from leaking to maps behind floating windows,
            // but allow OpenLayers interactions inside the window itself.
            const target = e.target instanceof Element ? e.target : null;
            if (!target?.closest('.ol-viewport')) {
                e.stopImmediatePropagation();
            }
        };

        ReactUtils.safeRender(
            <FloatingWindow
                ref={el => {
                    componentInstance = el;
                    if (el && windows[opts.uuid]) {
                        windows[opts.uuid].componentInstance = componentInstance;
                    }
                }}
                {...opts}
                onClose={() => {
                    WindowManager.close(opts.uuid);
                    Events.trigger(EventsGlobal.FLOATING_WINDOW_CLOSED, {
                        isMobile: isMobile(),
                    });
                }}
            >
                {opts.body}
            </FloatingWindow>,
            windowEl
        );

        Events.trigger(EventsGlobal.FLOATING_WINDOW_OPENED, {
            isMobile: isMobile(),
        });

        return windows[opts.uuid];
    },

    openUrl: (url: string) => {
        WindowManager.open({
            group: 'url',
            id: url,
            title: url,
            showCloseButton: true,
            resize: 'both',
            defaultSizing: { width: window.innerWidth * 0.5, height: window.innerHeight * 0.8 },
            body: <iframe src={url} style={{ width: '100%', height: '100%' }} />,
        });
    },

    close: (uuid?: string) => {
        if (windows[uuid]) {
            const windowEl = windows[uuid];
            delete windows[uuid];

            if (typeof windowEl.onClose === 'function') {
                windowEl.onClose();
            }
        }
        const existing = document.querySelector(`[data-window-uuid="${uuid}"`) as HTMLDivElement;
        if (existing) {
            ReactUtils.safeUnmountComponentAtNode(existing);
            existing.remove();
        }
    },

    closeAll: () => {
        Object.keys(windows).forEach(WindowManager.close);
    },

    getTopmostUuid: (): IFloatingWindow => {
        if (Object.keys(windows).length === 0) {
            return null;
        }
        if (Object.keys(windows).length === 1) {
            return windows[Object.keys(windows)[0]];
        }
        const windowEls: HTMLDivElement[] = Array.from(document.querySelectorAll(`[data-window-uuid]`));
        const zs = windowEls.map(el => parseInt(el.style.zIndex));
        const index = zs.indexOf(Math.max(...zs));
        return windows[windowEls[index]?.dataset.windowUuid];
    },

    getByGroupId(id: string): IFloatingWindow[] {
        return Object.values(windows).filter(w => w.group === id);
    },

    toggleMinimizeTopmost: () => {
        const topmostUuid = WindowManager.getTopmostUuid();
        const topmost: IFloatingWindow = topmostUuid ? windows[topmostUuid.uuid] : null;
        if (topmost) {
            topmost.componentInstance.toggleMinimize();
        }
    },
};

@observer
class FloatingWindow extends React.PureComponent<IFloatingWindow> {
    position: IFloatingWindowSizing = {
        x: ($('.js-sidebar').innerWidth() ?? 0) + 80,
        y: 120,
        width: Math.min(window.innerWidth * 0.4, 600),
        height: Math.min(window.innerHeight * 0.7, 800),
    };

    @observable show = false;
    @observable isDragging = false;
    @observable isMinimized = false;
    rndEL: Rnd = null;
    padding = 50;
    maxWidth = window.innerWidth - this.padding * 2;
    maxHeight = window.innerHeight - this.padding * 2;

    constructor(props) {
        super(props);
        makeObservable(this);
    }

    componentDidMount() {
        const autoLayout = !this.props.disableAutoLayout;
        let storage = autoLayout ? lastPositionStorage[this.props.group] : null;

        if (!storage && this.props.defaultSizing) {
            storage = this.props.defaultSizing;
        }

        if (storage) {
            const existing = autoLayout ? Array.from(document.querySelectorAll(`[data-window-group="${this.props.group}"`)) : [];
            if (storage.x) {
                this.position.x = storage.x + (existing.length > 1 ? window.innerWidth * 0.05 : 0);
                this.position.x = Math.min(this.position.x, window.innerWidth - this.padding);
            }
            if (storage.y) {
                this.position.y = storage.y + (existing.length > 1 ? window.innerHeight * 0.03 : 0);
                this.position.y = Math.min(this.position.y, window.innerHeight - this.padding);
            }
            if (this.props.resize !== null) {
                if (storage.width) {
                    this.position.width = Math.min(storage.width as number, this.maxWidth);
                }
                if (storage.height) {
                    this.position.height = Math.min(storage.height as number, this.maxHeight);
                }
            }
        }

        if (this.props.resize === null) {
            this.position.width = 'auto';
            this.position.height = 'auto';
        }

        this.updateStorage();
        this.show = true;
    }

    updateStorage = () => {
        lastPositionStorage[this.props.group] = {
            x: this.position.x,
            y: this.position.y,
            width: this.position.width,
            height: this.position.height,
        };
    };

    render() {
        if (!this.show) return null;
        const { children, title, collapsedTitle, showCloseButton } = this.props;
        const mobile = isMobile();
        const wrapper = wrapperEl();

        const Body = (
            <div className={cs('floating-window-body', { mobile, collapsed: this.isMinimized, card: !mobile })}>
                <div className='floating-window-header'>
                    {mobile && <Button secondary subtle icon={this.isMinimized ? 'arrowup' : 'arrowdown'} onClick={this.toggleMinimize} style={{ flexShrink: 0 }} />}

                    <div className='drag-area' onDoubleClick={this.toggleMaximized} onClick={mobile ? this.toggleMinimize : null}>
                        {(title || (collapsedTitle && this.isMinimized)) && <div className='floating-window-title'>{title || collapsedTitle}</div>}
                        {!mobile && <div className='drag-handle'></div>}
                    </div>
                    {showCloseButton && <Button secondary subtle icon='x' onClick={this.onClose} style={{ flexShrink: 0 }} />}
                </div>
                {children}
            </div>
        );

        if (mobile) {
            return Body;
        }

        return (
            <Rnd
                ref={el => (this.rndEL = el)}
                dragHandleClassName='drag-area'
                minWidth={400}
                minHeight={600}
                maxHeight={this.maxHeight}
                maxWidth={this.maxWidth}
                enableResizing={
                    this.props.resize
                        ? {
                              top: false,
                              right: this.props.resize === 'both' || this.props.resize === 'horizontal',
                              left: this.props.resize === 'both' || this.props.resize === 'horizontal',
                              bottom: this.props.resize === 'both' || this.props.resize === 'vertical',
                              topRight: false,
                              topLeft: this.props.resize === 'both',
                              bottomRight: this.props.resize === 'both',
                              bottomLeft: this.props.resize === 'both',
                          }
                        : null
                }
                default={{
                    x: this.position.x,
                    y: this.position.y,
                    width: this.position.width,
                    height: this.position.height,
                }}
                onResizeStart={() => {
                    this.isDragging = true;
                    wrapper.classList.add('is-dragging');
                }}
                onResizeStop={(e, direction, ref, delta, position) => {
                    this.position.width = ref.getBoundingClientRect().width;
                    this.position.height = ref.getBoundingClientRect().height;
                    this.isDragging = false;
                    wrapper.classList.remove('is-dragging');
                    this.updateStorage();
                }}
                onDragStart={() => {
                    this.isDragging = true;
                    wrapper.classList.add('is-dragging');
                }}
                onDragStop={(e, d) => {
                    this.isDragging = false;
                    this.position.x = utils.clamp(
                        this.padding - (typeof this.position.width === 'number' ? this.position.width - this.padding : 300),
                        d.x,
                        this.maxWidth + this.padding
                    );
                    this.position.y = utils.clamp(this.padding, d.y, this.maxHeight + this.padding);
                    wrapper.classList.remove('is-dragging');
                    this.refresh();
                    this.updateStorage();
                }}
            >
                {Body}
            </Rnd>
        );
    }

    onClose = () => {
        if (this.props.onClose) {
            this.props.onClose();
        }
    };

    toggleMinimize = () => {
        this.isMinimized = !this.isMinimized;
    };

    toggleMaximized = () => {
        if (this.props.resize === null || isMobile()) return;

        if (this.props.resize === 'both') {
            this.position.x = this.padding;
            this.position.y = this.padding;
            this.position.width = this.maxWidth;
            this.position.height = this.maxHeight;
        } else if (this.props.resize === 'horizontal') {
            this.position.width = this.maxWidth;
        } else if (this.props.resize === 'vertical') {
            this.position.y = this.padding;
            this.position.height = window.innerHeight - this.padding * 2;
        }

        this.refresh();
    };

    refresh = () => {
        this.rndEL.updateSize({
            width: this.position.width,
            height: this.position.height,
        });
        this.rndEL.updatePosition({
            x: this.position.x,
            y: this.position.y,
        });
    };
}
